import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { SmsCreditsSection } from '@/pages/sms-credits-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Listbox } from '@/components/ui/listbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

type SmsTransportMode = 'http_only' | 'smpp_only' | 'adaptive';
type SmsSection = 'routing' | 'credits';

interface PlatformSmsConfig {
  transportMode: SmsTransportMode;
  httpConfigured: boolean;
  smppConfigured: boolean;
  smppConnected: boolean;
}

const sectionFromPath = (pathname: string): SmsSection =>
  pathname.endsWith('/credits') ? 'credits' : 'routing';

const SmsRoutingSection = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [transportMode, setTransportMode] = useState<SmsTransportMode>('adaptive');

  const sms = useQuery({
    queryKey: ['platform-sms'],
    queryFn: () => api.get<PlatformSmsConfig>('/platform/sms'),
  });

  useEffect(() => {
    if (!sms.data) return;
    setTransportMode(sms.data.transportMode);
  }, [sms.data]);

  const save = useMutation({
    mutationFn: () => api.patch('/platform/sms', { transportMode }),
    onSuccess: async () => {
      toast.success(t('sms.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform-sms'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const modeHelp = t(`sms.modeHelp.${transportMode}`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('sms.routingTitle')}</CardTitle>
        <CardDescription>{t('sms.routingDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-body-sm font-medium">{t('sms.httpLabel')}</span>
            <Badge variant={sms.data?.httpConfigured ? 'success' : 'warning'}>
              {sms.data?.httpConfigured ? t('sms.configured') : t('sms.notConfigured')}
            </Badge>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-body-sm font-medium">{t('sms.smppLabel')}</span>
            <Badge variant={sms.data?.smppConfigured ? 'success' : 'warning'}>
              {sms.data?.smppConfigured ? t('sms.configured') : t('sms.notConfigured')}
            </Badge>
          </div>
        </div>

        {sms.data?.transportMode !== 'http_only' && sms.data?.smppConfigured ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-secondary px-3 py-2">
            <span className="text-body-sm text-fg-secondary">{t('sms.smppConnection')}</span>
            <Badge variant={sms.data.smppConnected ? 'success' : 'neutral'}>
              {sms.data.smppConnected ? t('sms.smppConnected') : t('sms.smppIdle')}
            </Badge>
          </div>
        ) : null}

        <FormField label={t('sms.transportMode')} hint={modeHelp}>
          {(field) => (
            <Listbox
              {...field}
              value={transportMode}
              onChange={(value) => value && setTransportMode(value)}
              options={[
                { value: 'http_only', label: t('sms.modes.http_only') },
                { value: 'adaptive', label: t('sms.modes.adaptive') },
                { value: 'smpp_only', label: t('sms.modes.smpp_only') },
              ]}
            />
          )}
        </FormField>

        {isAdmin ? (
          <div className="flex justify-end">
            <Button loading={save.isPending} disabled={sms.isLoading} onClick={() => save.mutate()}>
              {t('sms.save')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const SmsPage = () => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const navigate = useNavigate();
  const section = sectionFromPath(location.pathname);

  return (
    <div className="space-y-8">
      <PageHeader title={t('sms.title')} description={t('sms.description')} />
      <ReadOnlyNotice />

      <Tabs
        value={section}
        onValueChange={(value) => navigate(value === 'credits' ? '/sms/credits' : '/sms')}
      >
        <TabsList>
          <TabsTrigger value="routing">{t('sms.tabs.routing')}</TabsTrigger>
          <TabsTrigger value="credits">{t('sms.tabs.credits')}</TabsTrigger>
        </TabsList>

        <TabsContent value="routing">
          <SmsRoutingSection />
        </TabsContent>

        <TabsContent value="credits">
          <SmsCreditsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};
